import type { CameraAnimationProps } from "@/components/r3f/animations/camera-animation";
import type { RefObject } from "react";
import type { Camera } from "three";
import type {
  AnimationComputer,
  FlyWalkAnimation,
  FlyWalkRenderingAnimation,
} from "./flywalk-animation";
import type { TargetCameraView } from "./flywalk-to-pano-animation";
import { cameraView2cameraAnimation } from "./use-react-to-scene-events";

/**
 *
 * @param animationComputer optional computer object for the initial to-model animations
 * @param camera The camera that currently renders the scene
 * @param renderingAnimation The currently needed rendering animation, if any
 * @param pendingEvent The pending event that might trigger a camera animation
 * @param setFlyWalkAnimation Callback to set the current flywalk animation
 */
export function runToModelAnimation(
  animationComputer: AnimationComputer | undefined,
  camera: Camera,
  renderingAnimation: FlyWalkRenderingAnimation,
  pendingEvent: RefObject<TargetCameraView | null>,
  setFlyWalkAnimation: (animation: FlyWalkAnimation | null) => void,
): void {
  let targetCamera = animationComputer?.compute(camera);
  if (!targetCamera) {
    // For example when the user is in overview mode, the active element is
    // the point cloud, and the user clicks go-to-annotation on a CAD annotation,
    // we need both a camera animation (stored in this pending event) and a rendering
    // animation to change scene filter to CAD
    targetCamera = pendingEvent2CameraAnimation(pendingEvent);
  }
  const animation = {
    renderingAnimation,
    cameraAnimation: targetCamera,
  };
  // If this is the initial animation entering the mode, make it
  // last a bit longer
  if (animationComputer && animation.cameraAnimation) {
    animation.renderingAnimation.duration = INITIAL_ANIMATION_DURATION;
    animation.cameraAnimation.duration = INITIAL_ANIMATION_DURATION;
  }
  setFlyWalkAnimation(animation);
}

/**
 *
 * @param pendingEvent The pending event that might trigger a camera animation
 * @returns The camera animation props if a pending event exists, otherwise undefined
 */
export function pendingEvent2CameraAnimation(
  pendingEvent: RefObject<TargetCameraView | null>,
): CameraAnimationProps | undefined {
  let targetCameraView: TargetCameraView | undefined;
  if (pendingEvent.current) {
    targetCameraView = pendingEvent.current;
    pendingEvent.current = null;
  }
  return targetCameraView?.cameraView
    ? cameraView2cameraAnimation(
        targetCameraView.cameraView,
        targetCameraView.aspectRatio ?? 1,
      )
    : undefined;
}

const INITIAL_ANIMATION_DURATION = 1.5;
